import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { journalApi, accountsApi, partiesApi, reportsApi, getStoredUser, type JournalEntry, type Account } from '../api/client.js';
import { formatAmount } from '../components/Layout.js';
import { SkeletonCards, EmptyState, ErrorBanner } from '../components/States.js';
import AttachmentModal from '../components/AttachmentModal.js';
import Pagination from '../components/Pagination.js';
import ScopeBar from '../components/ScopeBar.js';
import { roleCan, permHint } from '../utils/permissions.js';

interface LineForm {
  accountCode: string;
  debit: string;
  credit: string;
  description: string;
}

const STATUS_LABELS: Record<string, { text: string; cls: string }> = {
  draft: { text: '草稿', cls: 'bg-slate-100 text-slate-600' },
  posted: { text: '已过账', cls: 'bg-blue-100 text-blue-700' },
  approved: { text: '已审核', cls: 'bg-green-100 text-green-700' },
  rejected: { text: '已驳回', cls: 'bg-red-100 text-red-700' },
};

const emptyLine = (): LineForm => ({ accountCode: '', debit: '', credit: '', description: '' });

export default function Journal() {
  const canCreate = roleCan('journal:create');
  const canReview = roleCan('journal:post');
  // 8.4.3 自助建档：录入时可就地快速建档案（accountant 建的强制挂自己）
  const me = getStoredUser();
  const myId = me?.id; // 稳定依赖：getStoredUser() 每次渲染返回新对象，直接进 deps 会触发重取风暴
  const canCreateParty = roleCan('party:create');
  // 账龄 chip 穿透：/journal?counterparty=X[&counterparty=Y] 按对手方精确筛选（⑩ 归户合并行多文本=命中其一）
  const [qParams, setQParams] = useSearchParams();
  const cpFilters = qParams.getAll('counterparty').filter(Boolean);
  const cpFilterKey = cpFilters.join('、'); // 渲染/依赖用的稳定快照
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [attachEntry, setAttachEntry] = useState<JournalEntry | null>(null); // 影像弹窗
  // 8.4 数据可见范围
  const [scope, setScope] = useState('all');
  const [scopeInfo, setScopeInfo] = useState<{ mode: 'all' | 'mine' | 'owner'; limited: boolean } | null>(null);

  // 查询筛选
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [total, setTotal] = useState(0);
  // 分页 + 关键字（防抖后入请求）
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  // 批量套打选择（跨页保留，便于按期间逐页勾选后一次打印）
  const [selected, setSelected] = useState<string[]>([]);
  const [printing, setPrinting] = useState(false);

  // 凭证表单
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState('');
  const [counterparty, setCounterparty] = useState('');
  // 往来单位档案建议（8.4.3 按归属分组标注：我的/公开/他人；仍允许自由文本，服务端按可见范围返回）
  const [partyNames, setPartyNames] = useState<Array<{ name: string; tag: string }>>([]);
  // 8.4.3 就地快速建档 mini 表单
  const [showQuick, setShowQuick] = useState(false);
  const [quickName, setQuickName] = useState('');
  const [quickType, setQuickType] = useState<'customer' | 'supplier'>('customer');
  const [quickSaving, setQuickSaving] = useState(false);
  // ⑨ 对手方文本命中曾用名→归一提示（仅 alias 命中时非 null）
  const [aliasHint, setAliasHint] = useState<{ alias: string; name: string } | null>(null);
  const [lines, setLines] = useState<LineForm[]>([emptyLine(), emptyLine()]);

  const totalDebit = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005 && totalDebit > 0;

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [jRes, aRes] = await Promise.all([
        journalApi.list({
          page,
          pageSize,
          keyword: keyword || undefined,
          status: statusFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          counterparty: cpFilters.length ? cpFilters : undefined,
          scope: scope !== 'all' ? scope : undefined,
        }),
        accountsApi.list({ pageSize: 200 }),
      ]);
      setEntries(jRes.entries);
      setTotal(jRes.total);
      setScopeInfo(jRes.scopeInfo ?? null);
      setAccounts(aRes.accounts);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, startDate, endDate, page, pageSize, keyword, cpFilterKey, scope]);

  useEffect(() => {
    // 8.4.3：拉可见范国内档案（受限角色=自持+公开）并按「我的→公开→他人」排序分组标注
    partiesApi.list({ isActive: true, pageSize: 500 })
      .then((r) => {
        const tagOf = (ownerUserId?: string | null) => (!ownerUserId ? '公开' : ownerUserId === myId ? '我的' : '他人');
        const order: Record<string, number> = { 我的: 0, 公开: 1, 他人: 2 };
        setPartyNames(
          r.parties
            .map((p) => ({ name: p.name, tag: tagOf(p.ownerUserId) }))
            .sort((a, b) => (order[a.tag] - order[b.tag]) || a.name.localeCompare(b.name, 'zh'))
        );
      })
      .catch(() => setPartyNames([]));
  }, [myId]);

  // ⑨ 对手方输入防抖 350ms 匹配档案：命中曾用名时提示归一，现名/未命中则清除提示
  useEffect(() => {
    const text = counterparty.trim();
    if (!text) { setAliasHint(null); return; }
    let alive = true;
    const timer = setTimeout(() => {
      partiesApi.match(text)
        .then((hit) => { if (alive) setAliasHint(hit && hit.matchedBy === 'alias' ? { alias: text, name: hit.party.name } : null); })
        .catch(() => { if (alive) setAliasHint(null); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [counterparty]);

  // 筛选/搜索变化回到第一页；关键字防抖 300ms
  useEffect(() => { setPage(1); }, [statusFilter, startDate, endDate, keyword, pageSize, cpFilterKey]);
  useEffect(() => {
    const t = setTimeout(() => setKeyword(kwInput.trim()), 300);
    return () => clearTimeout(t);
  }, [kwInput]);

  const updateLine = (idx: number, patch: Partial<LineForm>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await journalApi.create({
        entryDate,
        description,
        counterparty: counterparty.trim() || undefined,
        lines: lines
          .filter((l) => l.accountCode && (parseFloat(l.debit) || parseFloat(l.credit)))
          .map((l) => ({
            accountCode: l.accountCode,
            debit: parseFloat(l.debit) || 0,
            credit: parseFloat(l.credit) || 0,
            description: l.description || undefined,
          })),
      });
      setShowForm(false);
      setLines([emptyLine(), emptyLine()]);
      setDescription('');
      setCounterparty('');
      setAliasHint(null);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const doPost = async (id: string) => {
    try { await journalApi.post(id); await load(); } catch (e: any) { setError(e.message); }
  };

  const doApprove = async (id: string) => {
    try { await journalApi.approve(id, 'admin'); await load(); } catch (e: any) { setError(e.message); }
  };

  const doVoid = async (id: string, reference: string) => {
    if (!window.confirm(`确认冲销凭证 ${reference}？将生成一张借贷反向的红字冲销凭证。`)) return;
    try { await journalApi.void(id, 'admin'); await load(); } catch (e: any) { setError(e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-800">记账凭证</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={!canCreate && !showForm}
          title={permHint('journal:create')}
          className="btn btn-primary"
        >
          {showForm ? '取消' : '+ 录入凭证'}
        </button>
      </div>

      {/* 8.4 数据可见范围 */}
      <div className="mb-4">
        <ScopeBar value={scope} onChange={setScope} scopeInfo={scopeInfo} />
      </div>

      {/* 查询筛选栏 */}
      <div className="mb-4 p-3 bg-white rounded-xl shadow-sm flex items-end gap-3 flex-wrap">
        {cpFilterKey && (
          <button
            onClick={() => { qParams.delete('counterparty'); setQParams(qParams, { replace: true }); }}
            title="清除对手方筛选"
            className="px-2.5 py-1.5 rounded-lg text-xs bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 self-end"
          >
            对手方：{cpFilterKey}{cpFilters.length > 1 ? `（${cpFilters.length} 个文本）` : ''} ✕
          </button>
        )}
        <div className="min-w-[200px] flex-1 max-w-xs">
          <label className="block text-xs text-slate-500 mb-1">凭证号 / 摘要</label>
          <input
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            placeholder="如 DEM-2026-09 或 顾问费"
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-full"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">状态</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          >
            <option value="">全部</option>
            <option value="draft">草稿</option>
            <option value="posted">已过账</option>
            <option value="approved">已审核</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">起始日期</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">结束日期</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm" />
        </div>
        {(statusFilter || startDate || endDate || kwInput) && (
          <button
            onClick={() => { setStatusFilter(''); setStartDate(''); setEndDate(''); setKwInput(''); setKeyword(''); }}
            className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          >✕ 清除筛选</button>
        )}
        <span className="ml-auto text-xs text-slate-400">共 {total} 张凭证</span>
      </div>

      {error && (
        <div className="mb-4"><ErrorBanner message={error} /></div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-4 bg-white rounded-xl shadow-sm">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">业务日期</label>
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">凭证摘要</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="如：收到张三咨询费"
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full"
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-xs text-slate-500 mb-1">往来对手方（可选，客户/供应商，用于账龄按对手方汇总）</label>
            <input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              list="party-name-suggestions"
              placeholder="如：A客户、恒力供应商（可选自往来单位档案）"
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full max-w-md"
            />
            <datalist id="party-name-suggestions">
              {partyNames.map((p) => <option key={p.name} value={p.name} label={`${p.tag} · ${p.name}`} />)}
            </datalist>
            {canCreateParty && (
              <div className="mt-1.5 flex items-center gap-2 w-fit">
                {!showQuick ? (
                  <button
                    type="button"
                    onClick={() => { setQuickName(counterparty.trim()); setShowQuick(true); }}
                    className="text-xs text-blue-600 hover:underline"
                    title="新建档案将自动挂你为负责人（自助建档，8.4.2/8.4.3）"
                  >
                    🏢 对手方不在列表？就地快速建档
                  </button>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <input
                      value={quickName}
                      onChange={(e) => setQuickName(e.target.value)}
                      placeholder="档案名称，如：新客小刘"
                      maxLength={60}
                      className="border border-slate-300 rounded-lg px-2 py-1 text-xs w-44"
                    />
                    <select value={quickType} onChange={(e) => setQuickType(e.target.value as 'customer' | 'supplier')} className="border border-slate-300 rounded-lg px-1.5 py-1 text-xs bg-white">
                      <option value="customer">客户</option>
                      <option value="supplier">供应商</option>
                    </select>
                    <button
                      type="button"
                      disabled={!quickName.trim() || quickSaving}
                      onClick={async () => {
                        setQuickSaving(true);
                        try {
                          const p = await partiesApi.create({ name: quickName.trim(), type: quickType });
                          setPartyNames((prev) => [{ name: p.name, tag: p.ownerUserId ? (p.ownerUserId === myId ? '我的' : '他人') : '公开' }, ...prev]);
                          setCounterparty(p.name);
                          setShowQuick(false);
                        } catch (err: any) {
                          setError(err.message || '快速建档失败');
                        } finally {
                          setQuickSaving(false);
                        }
                      }}
                      className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:text-slate-400"
                    >
                      {quickSaving ? '建档中…' : '建档并选用'}
                    </button>
                    <button type="button" onClick={() => setShowQuick(false)} className="text-xs text-slate-400 hover:text-slate-600">取消</button>
                    <span className="text-[11px] text-slate-400">新档案自动挂你为负责人</span>
                  </span>
                )}
              </div>
            )}
            {aliasHint && (
              <div className="mt-1.5 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 w-fit max-w-md">
                <span>「{aliasHint.alias}」是「{aliasHint.name}」的曾用名，历史账龄/预警仍归该档案</span>
                <button
                  type="button"
                  onClick={() => { setCounterparty(aliasHint.name); setAliasHint(null); }}
                  className="shrink-0 font-medium text-amber-800 underline hover:text-amber-900"
                >
                  一键归一为「{aliasHint.name}」
                </button>
              </div>
            )}
          </div>

          {/* 分录行 */}
          <table className="w-full text-sm mb-3">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left px-2 py-2">科目</th>
                <th className="text-left px-2 py-2 w-32">借方金额</th>
                <th className="text-left px-2 py-2 w-32">贷方金额</th>
                <th className="text-left px-2 py-2">行摘要</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1">
                    <select
                      value={line.accountCode}
                      onChange={(e) => updateLine(idx, { accountCode: e.target.value })}
                      className="border border-slate-300 rounded px-2 py-1.5 text-sm w-full bg-white"
                    >
                      <option value="">选择科目</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.code}>{a.code} {a.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={line.debit}
                      onChange={(e) => updateLine(idx, { debit: e.target.value, credit: '' })}
                      placeholder="0.00"
                      className="border border-slate-300 rounded px-2 py-1.5 text-sm w-full"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={line.credit}
                      onChange={(e) => updateLine(idx, { credit: e.target.value, debit: '' })}
                      placeholder="0.00"
                      className="border border-slate-300 rounded px-2 py-1.5 text-sm w-full"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={line.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      className="border border-slate-300 rounded px-2 py-1.5 text-sm w-full"
                    />
                  </td>
                  <td className="px-2 py-1 text-center">
                    {lines.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                        className="text-red-400 hover:text-red-600"
                      >✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 借贷合计 */}
          <div className="flex items-center justify-between mb-3 text-sm">
            <button
              type="button"
              onClick={() => setLines([...lines, emptyLine()])}
              className="text-blue-600 hover:text-blue-800"
            >+ 添加分录行</button>
            <div className={`font-mono ${balanced ? 'text-green-600' : 'text-red-600'}`}>
              借 {formatAmount(totalDebit)} / 贷 {formatAmount(totalCredit)}
              {balanced ? ' ✓ 平衡' : totalDebit > 0 || totalCredit > 0 ? ' ✗ 不平衡' : ''}
            </div>
          </div>

          <button
            type="submit"
            disabled={!balanced || !canCreate}
            title={permHint('journal:create')}
            className={`px-4 py-2 rounded-lg text-sm text-white ${
              balanced ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            保存凭证
          </button>
        </form>
      )}

      {/* 凭证列表 */}
      <div className="space-y-3">
        {selected.length > 0 && (
          <div className="sticky top-2 z-10 bg-slate-800 text-white rounded-xl px-4 py-2.5 flex items-center gap-3 text-sm shadow-lg animate-fade-in-up">
            <span>已选 <b className="font-mono">{selected.length}</b> 张凭证</span>
            <button
              onClick={async () => {
                setPrinting(true);
                try {
                  await reportsApi.printVouchers(selected);
                } catch (e: any) {
                  setError(e.message || '套打失败');
                } finally {
                  setPrinting(false);
                }
              }}
              disabled={printing}
              className="btn btn-primary btn-sm"
              title="多张凭证连续套打，末尾自动附「原始附件清单」页"
            >
              {printing ? '生成中…' : '🖨 批量套打（含附件清单页）'}
            </button>
            <button
              onClick={() => {
                const ids = entries.map((e) => e.id);
                const all = ids.every((id) => selected.includes(id));
                setSelected(all ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
              }}
              className="text-xs text-white/70 hover:text-white"
            >
              {entries.length > 0 && entries.every((e) => selected.includes(e.id)) ? '取消本页' : '选择本页'}
            </button>
            <button onClick={() => setSelected([])} className="text-xs text-white/70 hover:text-white ml-auto">
              ✕ 清空选择
            </button>
          </div>
        )}
        {loading ? (
          <SkeletonCards count={Math.min(pageSize, 6)} className="space-y-3" />
        ) : entries.length === 0 && total > 0 ? (
          <EmptyState
            icon="🔍"
            title="本页无凭证"
            hint={`筛选结果共 ${total} 张，当前页码超出范围`}
            action={<button onClick={() => setPage(1)} className="btn btn-ghost">回到第一页</button>}
          />
        ) : entries.length === 0 ? (
          (keyword || statusFilter || startDate || endDate) ? (
            <EmptyState
              icon="🔍"
              title="没有符合条件的凭证"
              hint="调整关键字、状态或日期范围重试"
              action={<button onClick={() => { setStatusFilter(''); setStartDate(''); setEndDate(''); setKwInput(''); setKeyword(''); }} className="btn btn-ghost">✕ 清除筛选</button>}
            />
          ) : (
          <EmptyState
            icon="🧾"
            title="还没有凭证"
            hint="手动录入凭证，或从智能导入 / 微信流水自动生成"
            action={<button onClick={() => setShowForm(true)} disabled={!canCreate} title={permHint('journal:create')} className="btn btn-primary">+ 录入凭证</button>}
          />
        )
        ) : (
          entries.map((entry) => {
            const st = STATUS_LABELS[entry.status] || STATUS_LABELS.draft;
            return (
              <div key={entry.id} className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(entry.id)}
                      onChange={() => setSelected((p) => (p.includes(entry.id) ? p.filter((x) => x !== entry.id) : [...p, entry.id]))}
                      title="选中后可批量套打"
                      className="w-3.5 h-3.5 accent-[#6366f1] cursor-pointer"
                    />
                    <span className="font-mono text-sm text-slate-500">{entry.reference}</span>
                    <span className="text-sm font-medium">{entry.description || '（无摘要）'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`}>{st.text}</span>
                    {entry.reversedById && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">已冲销</span>
                    )}
                    {entry.reversesId && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">↩ 冲销凭证</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{entry.entryDate}</span>
                    {entry.status === 'draft' && (
                      <button onClick={() => doPost(entry.id)} disabled={!canReview} title={permHint('journal:post')} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 disabled:text-slate-400 disabled:bg-slate-100 disabled:cursor-not-allowed">
                        过账
                      </button>
                    )}
                    {entry.status === 'posted' && (
                      <button onClick={() => doApprove(entry.id)} disabled={!canReview} title={permHint('journal:post')} className="text-xs px-2 py-1 bg-green-50 text-green-600 rounded hover:bg-green-100 disabled:text-slate-400 disabled:bg-slate-100 disabled:cursor-not-allowed">
                        审核
                      </button>
                    )}
                    {(entry.status === 'posted' || entry.status === 'approved') && !entry.reversedById && (
                      <button onClick={() => doVoid(entry.id, entry.reference)} disabled={!canReview} title={permHint('journal:post')} className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 disabled:text-slate-400 disabled:bg-slate-100 disabled:cursor-not-allowed">
                        冲销
                      </button>
                    )}
                    <button
                      onClick={() => setAttachEntry(entry)}
                      title="原始凭证影像"
                      className="text-xs px-2 py-1 bg-slate-50 text-slate-500 rounded hover:bg-slate-100"
                    >
                      📎{entry.attachmentCount ? ` ${entry.attachmentCount}` : ''}
                    </button>
                    <button
                      onClick={() => reportsApi.printVoucher(entry.id)}
                      title="打印凭证"
                      className="text-xs px-2 py-1 bg-slate-50 text-slate-500 rounded hover:bg-slate-100"
                    >
                      🖨 打印
                    </button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr>
                      <th className="text-left py-1">科目</th>
                      <th className="text-right py-1">借方</th>
                      <th className="text-right py-1">贷方</th>
                      <th className="text-left py-1 pl-4">摘要</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.lines.map((l) => (
                      <tr key={l.id} className="border-t border-slate-50">
                        <td className="py-1 font-mono">{l.accountCode} {l.accountName}</td>
                        <td className="py-1 text-right font-mono">{l.debit ? formatAmount(l.debit) : ''}</td>
                        <td className="py-1 text-right font-mono">{l.credit ? formatAmount(l.credit) : ''}</td>
                        <td className="py-1 pl-4 text-slate-500">{l.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })
        )}
      </div>

      {!loading && total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          unit="张凭证"
          onPage={setPage}
          onPageSize={setPageSize}
        />
      )}

      {attachEntry && (
        <AttachmentModal
          key={attachEntry.id}
          entryId={attachEntry.id}
          reference={attachEntry.reference}
          onClose={() => {
            setAttachEntry(null);
            load(); // 上传/删除后同步 📎 计数
          }}
        />
      )}
    </div>
  );
}
